/**
 * 게시판 스팸·법규 패턴 판정 (FINAL_BOARD v3 §1-5·§2-3·§2-6 · 게이트 G11)
 *
 * 순수 함수. 서버 쓰기 API 가 부른다(클라이언트 판정은 참고용일 뿐 — 서버가 최종).
 * 사전(금칙어)은 app_config board/banned_words 로 배포 없이 추가할 수 있다(extraBanned 인자).
 *
 * 판정 종류(G11 10종):
 *   url · phone · messenger(카톡ID·오픈채팅·텔레그램) · email · listing(매물 — 공인중개사법 18조의2) ·
 *   leading(리딩방·수익 보장 — 자본시장법) · adult(도박·성인) · flood(도배·반복) · banned(금칙어 사전) ·
 *   markup(이미지·HTML·스크립트 삽입)
 */

export type SpamKind =
  | 'url' | 'phone' | 'messenger' | 'email' | 'listing'
  | 'leading' | 'adult' | 'flood' | 'banned' | 'markup';

export const SPAM_KIND_MESSAGE: Record<SpamKind, string> = {
  url: '링크는 등급이 오르면 쓸 수 있어요(게스트는 텍스트만).',
  phone: '전화번호는 올릴 수 없어요.',
  messenger: '메신저 ID·오픈채팅 주소는 올릴 수 없어요.',
  email: '이메일 주소는 올릴 수 없어요.',
  listing: '매물 광고는 올릴 수 없어요(공인중개사법).',
  leading: '투자 리딩·수익 보장 홍보는 올릴 수 없어요(자본시장법).',
  adult: '도박·성인 관련 내용은 올릴 수 없어요.',
  flood: '같은 글자·문장을 반복한 글은 올릴 수 없어요.',
  banned: '사용할 수 없는 표현이 들어 있어요.',
  markup: '이미지·HTML 태그는 올릴 수 없어요.',
};

/** 난독화 정규화: 전각→반각, 제로폭 제거, 연속 공백 1칸. 판정용 사본에만 쓴다(저장본은 원문). */
export function normalizeForScan(input: string): string {
  return (input ?? '')
    .normalize('NFKC')
    .replace(/[​-‍⁠﻿­]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export const URL_RE = /(https?:\/\/|www\.)\S+|\b[a-z0-9-]{2,}\.(com|net|org|kr|co\.kr|io|me|ly|gl|to|xyz|shop|site|app|link|biz|info|tv|cc)\b(\/\S*)?/i;
// 숫자 사이 공백·점·하이픈·괄호 허용(010 1234 5678 · 010.1234.5678 · (02)123-4567).
export const PHONE_RE = /(?:^|[^\d])(?:0\d{1,2})[\s.\-)]{0,3}\d{3,4}[\s.\-]{0,3}\d{4}(?!\d)/;
// 한글로 적은 휴대폰 번호(공일공…) — 숫자 한글 8자 이상 연속.
export const PHONE_KO_RE = /[공영일이삼사오육칠팔구]{9,}/;
export const EMAIL_RE = /[a-z0-9._%+-]+\s*(@|＠|\(at\)|\[at\])\s*[a-z0-9.-]+\.[a-z]{2,}/i;
export const MESSENGER_RE = /(카톡|카카오톡|kakao|katalk|텔레그램|telegram|라인|위챗|wechat|디스코드|discord)\s*(아이디|id)\s*[:：]?\s*[a-z0-9_.@-]{3,}|(카톡|텔레|텔레그램)\s*[:：]\s*[a-z0-9_.@-]{3,}|오픈\s*채팅\s*(방|주소|링크|입장)|오픈톡|open\.kakao|t\.me\//i;
const LISTING_RE = /((매물|급매|급전세|전세|월세|분양권|상가|오피스텔)\s*(있습니다|있어요|내놓|보유\s*중|소개\s*(합니다|해\s*드)|문의\s*(주세요|환영|바랍니다)|연락\s*(주세요|바랍니다))|분양권\s*(판매|팝니다|양도\s*합니다)|직거래\s*(합니다|원해요)|중개\s*수수료\s*(무료|할인)|(피|프리미엄|P)\s*\d+\s*(만|천)\s*원?\s*(에|로)?\s*(팝니다|넘깁니다|양도))/i;
const LEADING_RE = /(리딩\s*방|리딩방|무료\s*리딩|종목\s*(무료\s*)?추천\s*(방|해\s*드|드립)|수익\s*(보장|확정)|원금\s*보장|월\s*\d+\s*%\s*수익|급등주\s*(무료|공유)|VIP\s*(방|리딩)|단톡방\s*(입장|초대))/i;
const ADULT_RE = /(카지노|바카라|토토|슬롯\s*사이트|먹튀|배팅|베팅\s*사이트|홀덤\s*사이트|조건\s*만남|출장\s*(안마|마사지)|성인\s*(방송|사이트)|야동|19금\s*사이트)/i;
const MARKUP_RE = /<\s*(img|script|iframe|a|style|object|embed|svg)\b|!\[[^\]]*\]\([^)]*\)|\[img\]|javascript:/i;
/** 기본 금칙어(최소). 운영 사전은 app_config board/banned_words 로 늘린다. */
const BASE_BANNED = ['씨발', '시발', '좆', '병신', '개새끼', '느금마', '니애미'];

/** 같은 글자 10회 이상, 같은 3글자 이상 조각 6회 이상, 같은 줄 4회 이상. */
export function isFlood(text: string): boolean {
  const t = text ?? '';
  if (/(.)\1{9,}/u.test(t)) return true;
  if (/(.{3,12}?)\1{5,}/u.test(t.replace(/\s+/g, ''))) return true;
  const lines = t.split(/\n+/).map((l) => l.trim()).filter((l) => l.length >= 4);
  const counts = new Map<string, number>();
  for (const l of lines) {
    const n = (counts.get(l) ?? 0) + 1;
    if (n >= 4) return true;
    counts.set(l, n);
  }
  return false;
}

export function countLinks(text: string): number {
  const m = normalizeForScan(text).match(new RegExp(URL_RE.source, 'gi'));
  return m ? m.length : 0;
}

export interface SpamScanOptions {
  /** 허용 링크 수(권한 매트릭스). 게스트 0. */
  allowLinks: number;
  /** app_config board/banned_words 추가 사전. */
  extraBanned?: readonly string[];
}

/** 첫 번째로 걸린 종류(없으면 null). 순서 = 법규 위반 → 연락처 → 형식. */
export function scanSpam(text: string, opts: SpamScanOptions): SpamKind | null {
  const raw = text ?? '';
  const t = normalizeForScan(raw);
  if (!t) return null;
  if (MARKUP_RE.test(raw) || MARKUP_RE.test(t)) return 'markup';
  if (ADULT_RE.test(t)) return 'adult';
  if (LEADING_RE.test(t)) return 'leading';
  if (LISTING_RE.test(t)) return 'listing';
  if (MESSENGER_RE.test(t)) return 'messenger';
  if (EMAIL_RE.test(t)) return 'email';
  if (PHONE_RE.test(t) || PHONE_KO_RE.test(t.replace(/\s/g, ''))) return 'phone';
  if (countLinks(t) > Math.max(0, opts.allowLinks)) return 'url';
  if (isFlood(raw)) return 'flood';
  const compact = t.replace(/\s/g, '');
  const dict = [...BASE_BANNED, ...(opts.extraBanned ?? [])].map((w) => String(w).trim()).filter(Boolean);
  if (dict.some((w) => compact.includes(w.replace(/\s/g, '')))) return 'banned';
  return null;
}

/** 설명(description)·목록 발췌용 연락처 마스킹(§2-8). */
export function maskContacts(text: string): string {
  return (text ?? '')
    .replace(new RegExp(EMAIL_RE.source, 'gi'), '(이메일 가림)')
    .replace(/0\d{1,2}[\s.\-)]{0,3}\d{3,4}[\s.\-]{0,3}\d{4}/g, '(번호 가림)');
}
